import { useState, useEffect, useCallback } from 'react';
import { FileText, Plus, Trash2, Zap, RefreshCw, ChevronLeft, Eye, Check, Send, Image, Newspaper, Key, BarChart3, PenTool } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { authFetch } from '../../lib/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const f = (url, opts = {}) => authFetch(`${API}${url}`, opts);
const fj = (url, body, method = 'POST') => f(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

const STATUS_MAP = {
  draft: { label: 'Brouillon', cls: 'bg-[#71717a]/10 text-[#a1a1aa]' },
  approved: { label: 'Approuve', cls: 'bg-amber-500/10 text-amber-400' },
  published: { label: 'Publie', cls: 'bg-green-500/10 text-green-400' },
};

const TABS = [
  { id: 'articles', label: 'Articles', icon: FileText },
  { id: 'pipeline', label: 'Pipeline', icon: Zap },
  { id: 'news', label: 'Actualites', icon: Newspaper },
  { id: 'prompts', label: 'Prompts', icon: PenTool },
  { id: 'stats', label: 'Stats', icon: BarChart3 },
];

const AdminBlog = () => {
  const [tab, setTab] = useState('articles');
  const [articles, setArticles] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [topics, setTopics] = useState([]);
  const [briefs, setBriefs] = useState([]);
  const [news, setNews] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState('');
  const [newKeyword, setNewKeyword] = useState('');

  const load = useCallback(async (what) => {
    const loaders = {
      articles: () => f('/api/seo/articles').then(r => r.json()).then(setArticles),
      keywords: () => f('/api/seo/keywords').then(r => r.json()).then(setKeywords),
      topics: () => f('/api/seo/topics').then(r => r.json()).then(setTopics),
      briefs: () => f('/api/seo/briefs').then(r => r.json()).then(setBriefs),
      news: () => f('/api/seo/news').then(r => r.json()).then(setNews),
      prompts: () => f('/api/seo/prompts').then(r => r.json()).then(setPrompts),
      stats: () => f('/api/seo/analytics').then(r => r.json()).then(setStats),
    };
    try { await loaders[what]?.(); } catch {}
  }, []);

  useEffect(() => {
    load('articles');
    load('keywords');
  }, [load]);

  useEffect(() => {
    if (tab === 'pipeline') { load('keywords'); load('topics'); load('briefs'); }
    if (tab === 'news') load('news');
    if (tab === 'prompts') load('prompts');
    if (tab === 'stats') load('stats');
  }, [tab, load]);

  const addKeyword = async () => {
    if (!newKeyword.trim()) return;
    await fj('/api/seo/keywords', { keyword: newKeyword.trim() });
    setNewKeyword('');
    load('keywords');
  };

  const deleteKeyword = async (id) => {
    await f(`/api/seo/keywords/${id}`, { method: 'DELETE' });
    load('keywords');
  };

  const generateTopics = async () => {
    setLoading('topics');
    try {
      await fj('/api/seo/generate-topics', { count: 5, include_news: true });
      load('topics');
    } catch {} finally { setLoading(''); }
  };

  const generateBrief = async (topicId) => {
    setLoading(`brief-${topicId}`);
    try {
      await fj('/api/seo/generate-brief', { topic_id: topicId });
      load('briefs');
    } catch {} finally { setLoading(''); }
  };

  const generateArticle = async (briefId) => {
    setLoading(`article-${briefId}`);
    try {
      await fj('/api/seo/generate-article', { brief_id: briefId });
      load('articles');
      setTab('articles');
    } catch {} finally { setLoading(''); }
  };

  const scanNews = async () => {
    setLoading('scan');
    try {
      await fj('/api/seo/scan-sources', {});
      load('news');
    } catch {} finally { setLoading(''); }
  };

  const approveArticle = async (id) => {
    await fj(`/api/seo/articles/${id}/approve`, {});
    load('articles');
    if (selected?.id === id) setSelected(s => ({ ...s, status: 'approved' }));
  };

  const publishArticle = async (id) => {
    await fj(`/api/seo/articles/${id}/publish`, {});
    load('articles');
    if (selected?.id === id) setSelected(s => ({ ...s, status: 'published' }));
  };

  const deleteArticle = async (id) => {
    await f(`/api/seo/articles/${id}`, { method: 'DELETE' });
    setSelected(null);
    load('articles');
  };

  const saveArticle = async () => {
    if (!selected) return;
    setLoading('save');
    try {
      await fj(`/api/seo/articles/${selected.id}`, {
        title: selected.title, slug: selected.slug, meta_title: selected.meta_title,
        meta_description: selected.meta_description, content: selected.content,
        hero_image: selected.hero_image, hero_image_alt: selected.hero_image_alt,
        editor_notes: selected.editor_notes,
      }, 'PUT');
      load('articles');
    } catch {} finally { setLoading(''); }
  };

  const searchImages = async () => {
    if (!selected) return;
    setLoading('images');
    try {
      const res = await fj(`/api/seo/articles/${selected.id}/search-images`, { query: selected.keyword || '' });
      const data = await res.json();
      setSelected(s => ({ ...s, image_proposals: data }));
    } catch {} finally { setLoading(''); }
  };

  const selectImage = async (img) => {
    if (!selected) return;
    await fj(`/api/seo/articles/${selected.id}/select-image`, { url: img.url, alt: img.description || '', credit: '' });
    setSelected(s => ({ ...s, hero_image: img.url, hero_image_alt: img.description || '' }));
  };

  const savePrompt = async (key, value) => {
    await fj(`/api/seo/prompts/${key}`, { value }, 'PUT');
    load('prompts');
  };

  const resetPrompt = async (key) => {
    await fj(`/api/seo/prompts/${key}/reset`, {});
    load('prompts');
  };

  const deleteTopic = async (id) => {
    await f(`/api/seo/topics/${id}`, { method: 'DELETE' });
    load('topics');
  };

  // ============ ARTICLE DETAIL VIEW ============
  if (selected) {
    const s = STATUS_MAP[selected.status] || STATUS_MAP.draft;
    return (
      <div className="p-6 md:p-8" data-testid="admin-blog-detail">
        <button onClick={() => setSelected(null)} className="flex items-center gap-2 text-[#71717a] hover:text-[#a1a1aa] text-sm mb-6" data-testid="blog-back-btn">
          <ChevronLeft className="w-4 h-4" /> Retour aux articles
        </button>

        <div className="flex items-center gap-3 mb-6">
          <h1 className="text-xl font-bold text-[#f5f5f5] flex-1">Modifier l'article</h1>
          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${s.cls}`}>{s.label}</span>
          {selected.status === 'draft' && (
            <Button onClick={() => approveArticle(selected.id)} className="bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 text-xs" data-testid="approve-btn">
              <Check className="w-3.5 h-3.5 mr-1" /> Approuver
            </Button>
          )}
          {(selected.status === 'approved' || selected.status === 'draft') && (
            <Button onClick={() => publishArticle(selected.id)} className="bg-green-500/20 text-green-400 hover:bg-green-500/30 text-xs" data-testid="publish-btn">
              <Send className="w-3.5 h-3.5 mr-1" /> Publier
            </Button>
          )}
          <Button onClick={saveArticle} disabled={loading === 'save'} className="bg-[#3b82f6] hover:bg-[#2563eb] text-white text-xs" data-testid="save-article-btn">
            {loading === 'save' ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
          <Button onClick={() => deleteArticle(selected.id)} className="bg-red-500/10 text-red-400 hover:bg-red-500/20 text-xs" data-testid="delete-article-btn">
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-4">
            <Field label="Titre" value={selected.title} onChange={v => setSelected(s => ({ ...s, title: v }))} testId="field-title" />
            <Field label="Slug" value={selected.slug} onChange={v => setSelected(s => ({ ...s, slug: v }))} testId="field-slug" />
            <Field label="Meta Title" value={selected.meta_title} onChange={v => setSelected(s => ({ ...s, meta_title: v }))} testId="field-meta-title" />
            <Field label="Meta Description" value={selected.meta_description} onChange={v => setSelected(s => ({ ...s, meta_description: v }))} testId="field-meta-desc" textarea rows={2} />
            <div>
              <label className="block text-[#a1a1aa] text-xs mb-1.5">Contenu (HTML)</label>
              <textarea
                value={selected.content || ''}
                onChange={e => setSelected(s => ({ ...s, content: e.target.value }))}
                rows={16}
                className="w-full px-3 py-2 rounded-lg bg-[#1a1a2e] border border-white/10 text-[#f5f5f5] text-sm font-mono focus:outline-none focus:border-[#3b82f6]"
                data-testid="field-content"
              />
            </div>
            <Field label="Notes editeur" value={selected.editor_notes || ''} onChange={v => setSelected(s => ({ ...s, editor_notes: v }))} testId="field-notes" textarea rows={3} />

            {/* FAQ */}
            {selected.faq?.length > 0 && (
              <div className="rounded-xl border border-white/5 bg-[#0f0f17] p-4">
                <h3 className="text-[#f5f5f5] text-sm font-semibold mb-3">FAQ ({selected.faq.length})</h3>
                <div className="space-y-3">
                  {selected.faq.map((faq, i) => (
                    <div key={i} className="border-l-2 border-[#3b82f6]/30 pl-3">
                      <p className="text-[#f5f5f5] text-sm font-medium">{faq.question}</p>
                      <p className="text-[#71717a] text-xs mt-1">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Hero Image */}
            <div className="rounded-xl border border-white/5 bg-[#0f0f17] p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-[#f5f5f5] text-sm font-semibold">Image hero</h3>
                <Button onClick={searchImages} disabled={loading === 'images'} className="text-xs bg-[#1a1a2e] text-[#a1a1aa] hover:text-white" data-testid="search-images-btn">
                  <Image className="w-3.5 h-3.5 mr-1" /> {loading === 'images' ? '...' : 'Chercher'}
                </Button>
              </div>
              {selected.hero_image ? (
                <img src={selected.hero_image} alt={selected.hero_image_alt || ''} className="w-full rounded-lg mb-2 aspect-video object-cover" />
              ) : (
                <div className="w-full aspect-video rounded-lg bg-[#1a1a2e] flex items-center justify-center mb-2">
                  <p className="text-[#52525b] text-xs">Aucune image</p>
                </div>
              )}
              <Field label="URL image" value={selected.hero_image || ''} onChange={v => setSelected(s => ({ ...s, hero_image: v }))} testId="field-hero-url" />

              {/* Image proposals */}
              {selected.image_proposals?.length > 0 && (
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {selected.image_proposals.map((img) => (
                    <button key={img.id} onClick={() => selectImage(img)} className="rounded-lg overflow-hidden border border-white/5 hover:border-[#3b82f6]/50 transition-colors">
                      <img src={img.url} alt={img.description} className="w-full aspect-video object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="rounded-xl border border-white/5 bg-[#0f0f17] p-4 space-y-2">
              <InfoRow label="Mot-cle" value={selected.keyword} />
              <InfoRow label="Mots" value={selected.word_count} />
              <InfoRow label="Cree le" value={selected.created_at ? new Date(selected.created_at).toLocaleDateString('fr-FR') : '-'} />
              {selected.published_at && <InfoRow label="Publie le" value={new Date(selected.published_at).toLocaleDateString('fr-FR')} />}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============ TABS VIEW ============
  return (
    <div className="p-6 md:p-8" data-testid="admin-blog">
      <h1 className="text-2xl font-bold text-[#f5f5f5] mb-6">Blog SEO</h1>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-white/5 pb-px" data-testid="blog-tabs">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
              tab === t.id ? 'bg-[#0f0f17] text-[#f5f5f5] border border-white/5 border-b-transparent -mb-px' : 'text-[#71717a] hover:text-[#a1a1aa]'
            }`}
            data-testid={`tab-${t.id}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* ======== ARTICLES TAB ======== */}
      {tab === 'articles' && (
        <div className="rounded-xl border border-white/5 bg-[#0f0f17] overflow-hidden" data-testid="articles-list">
          {articles.length === 0 ? (
            <p className="p-8 text-center text-[#71717a] text-sm">Aucun article. Utilisez le Pipeline pour en generer.</p>
          ) : (
            <div className="divide-y divide-white/5">
              {articles.map(a => {
                const st = STATUS_MAP[a.status] || STATUS_MAP.draft;
                return (
                  <button key={a.id} onClick={() => setSelected(a)} className="w-full px-5 py-3.5 flex items-center gap-4 hover:bg-[#1a1a2e]/30 transition-colors text-left" data-testid={`article-${a.id}`}>
                    {a.hero_image ? (
                      <img src={a.hero_image} alt="" className="w-16 h-10 rounded object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-16 h-10 rounded bg-[#1a1a2e] flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-[#f5f5f5] text-sm font-medium truncate">{a.title}</p>
                      <p className="text-[#52525b] text-xs mt-0.5">{a.keyword} &middot; {a.word_count || 0} mots</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span>
                    <span className="text-[#52525b] text-xs">{a.created_at ? new Date(a.created_at).toLocaleDateString('fr-FR') : ''}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ======== PIPELINE TAB ======== */}
      {tab === 'pipeline' && (
        <div className="space-y-8" data-testid="pipeline-tab">
          {/* Keywords */}
          <section>
            <h2 className="text-[#f5f5f5] font-semibold mb-3 flex items-center gap-2"><Key className="w-4 h-4 text-[#3b82f6]" /> Mots-cles ({keywords.length})</h2>
            <div className="flex gap-2 mb-3">
              <input
                value={newKeyword}
                onChange={e => setNewKeyword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addKeyword()}
                placeholder="Ajouter un mot-cle..."
                className="flex-1 px-3 py-2 rounded-lg bg-[#1a1a2e] border border-white/10 text-[#f5f5f5] text-sm focus:outline-none focus:border-[#3b82f6] placeholder-[#52525b]"
                data-testid="keyword-input"
              />
              <Button onClick={addKeyword} className="bg-[#3b82f6] text-white text-sm" data-testid="add-keyword-btn"><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {keywords.map(k => (
                <span key={k.id} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1a1a2e] text-[#a1a1aa] text-sm">
                  {k.keyword}
                  <button onClick={() => deleteKeyword(k.id)} className="text-[#52525b] hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          </section>

          {/* Topics */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-[#f5f5f5] font-semibold flex items-center gap-2"><Zap className="w-4 h-4 text-amber-400" /> Sujets ({topics.length})</h2>
              <Button onClick={generateTopics} disabled={loading === 'topics'} className="bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 text-xs" data-testid="generate-topics-btn">
                {loading === 'topics' ? <><RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> Generation...</> : <><Zap className="w-3.5 h-3.5 mr-1" /> Generer des sujets</>}
              </Button>
            </div>
            {topics.length > 0 && (
              <div className="space-y-2">
                {topics.map(t => (
                  <div key={t.id} className="flex items-center gap-3 px-4 py-3 rounded-xl border border-white/5 bg-[#0f0f17]" data-testid={`topic-${t.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-[#f5f5f5] text-sm font-medium">{t.title}</p>
                      <p className="text-[#52525b] text-xs mt-0.5">{t.keyword} &middot; {t.angle}</p>
                    </div>
                    <Button onClick={() => generateBrief(t.id)} disabled={loading === `brief-${t.id}`} className="bg-[#3b82f6]/10 text-[#3b82f6] hover:bg-[#3b82f6]/20 text-xs" data-testid={`gen-brief-${t.id}`}>
                      {loading === `brief-${t.id}` ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : 'Brief'}
                    </Button>
                    <button onClick={() => deleteTopic(t.id)} className="text-[#52525b] hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Briefs */}
          <section>
            <h2 className="text-[#f5f5f5] font-semibold mb-3 flex items-center gap-2"><FileText className="w-4 h-4 text-green-400" /> Briefs ({briefs.length})</h2>
            {briefs.length > 0 && (
              <div className="space-y-2">
                {briefs.map(b => (
                  <div key={b.id} className="px-4 py-3 rounded-xl border border-white/5 bg-[#0f0f17]" data-testid={`brief-${b.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[#f5f5f5] text-sm font-medium">{b.topic_title}</p>
                      <Button onClick={() => generateArticle(b.id)} disabled={loading === `article-${b.id}`} className="bg-green-500/10 text-green-400 hover:bg-green-500/20 text-xs" data-testid={`gen-article-${b.id}`}>
                        {loading === `article-${b.id}` ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : 'Rediger'}
                      </Button>
                    </div>
                    <p className="text-[#71717a] text-xs">/{b.suggested_slug} &middot; {b.h2_outline?.length || 0} H2 &middot; {b.faq_suggestions?.length || 0} FAQ</p>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {/* ======== NEWS TAB ======== */}
      {tab === 'news' && (
        <div data-testid="news-tab">
          <div className="flex items-center justify-between mb-4">
            <p className="text-[#71717a] text-sm">{news.length} actualites</p>
            <Button onClick={scanNews} disabled={loading === 'scan'} className="bg-[#3b82f6] text-white text-xs" data-testid="scan-news-btn">
              {loading === 'scan' ? <><RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> Scan...</> : <><RefreshCw className="w-3.5 h-3.5 mr-1" /> Scanner les sources</>}
            </Button>
          </div>
          <div className="rounded-xl border border-white/5 bg-[#0f0f17] divide-y divide-white/5">
            {news.length === 0 ? (
              <p className="p-8 text-center text-[#71717a] text-sm">Aucune actualite. Cliquez sur Scanner.</p>
            ) : news.map((n, i) => (
              <div key={i} className="px-5 py-3">
                <div className="flex items-center justify-between">
                  <a href={n.url} target="_blank" rel="noopener noreferrer" className="text-[#f5f5f5] text-sm font-medium hover:text-[#3b82f6] transition-colors">{n.title}</a>
                  <span className="text-[#52525b] text-xs ml-2 flex-shrink-0">{n.source_name}</span>
                </div>
                {n.summary && <p className="text-[#71717a] text-xs mt-1 line-clamp-2">{n.summary.replace(/<[^>]*>/g, '').slice(0, 200)}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ======== PROMPTS TAB ======== */}
      {tab === 'prompts' && (
        <div className="space-y-6 max-w-3xl" data-testid="prompts-tab">
          {prompts.map(p => (
            <PromptEditor key={p.key} prompt={p} onSave={savePrompt} onReset={resetPrompt} />
          ))}
        </div>
      )}

      {/* ======== STATS TAB ======== */}
      {tab === 'stats' && stats && (
        <div data-testid="stats-tab">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[
              { l: 'Articles total', v: stats.total_articles },
              { l: 'Publies', v: stats.published },
              { l: 'Brouillons', v: stats.drafts },
              { l: 'Vues blog (30j)', v: stats.blog_views_30d },
            ].map(s => (
              <div key={s.l} className="p-4 rounded-xl border border-white/5 bg-[#0f0f17]">
                <p className="text-[#71717a] text-xs mb-1">{s.l}</p>
                <p className="text-xl font-bold text-[#f5f5f5]">{s.v}</p>
              </div>
            ))}
          </div>
          {stats.top_articles?.length > 0 && (
            <div className="rounded-xl border border-white/5 bg-[#0f0f17] overflow-hidden">
              <div className="p-4 border-b border-white/5"><h3 className="text-[#f5f5f5] font-semibold text-sm">Top articles (30j)</h3></div>
              <div className="divide-y divide-white/5">
                {stats.top_articles.map((a, i) => (
                  <div key={i} className="px-5 py-2.5 flex justify-between">
                    <span className="text-[#a1a1aa] text-sm">{a.page}</span>
                    <span className="text-[#f5f5f5] text-sm font-medium">{a.views} vues</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============ SMALL COMPONENTS ============

const Field = ({ label, value, onChange, testId, textarea, rows = 1 }) => (
  <div>
    <label className="block text-[#a1a1aa] text-xs mb-1.5">{label}</label>
    {textarea ? (
      <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={rows}
        className="w-full px-3 py-2 rounded-lg bg-[#1a1a2e] border border-white/10 text-[#f5f5f5] text-sm focus:outline-none focus:border-[#3b82f6]"
        data-testid={testId} />
    ) : (
      <input value={value || ''} onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg bg-[#1a1a2e] border border-white/10 text-[#f5f5f5] text-sm focus:outline-none focus:border-[#3b82f6]"
        data-testid={testId} />
    )}
  </div>
);

const InfoRow = ({ label, value }) => (
  <div className="flex justify-between">
    <span className="text-[#71717a] text-xs">{label}</span>
    <span className="text-[#a1a1aa] text-xs font-medium">{value}</span>
  </div>
);

const PromptEditor = ({ prompt, onSave, onReset }) => {
  const [val, setVal] = useState(prompt.value);
  const labels = { topic_generation: 'Generation de sujets', brief_generation: 'Generation de briefs', article_generation: 'Generation d\'articles' };
  return (
    <div className="rounded-xl border border-white/5 bg-[#0f0f17] p-5" data-testid={`prompt-${prompt.key}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[#f5f5f5] text-sm font-semibold">{labels[prompt.key] || prompt.key}</h3>
        {prompt.is_custom && <span className="text-xs text-amber-400">Personnalise</span>}
      </div>
      <textarea value={val} onChange={e => setVal(e.target.value)} rows={6}
        className="w-full px-3 py-2 rounded-lg bg-[#1a1a2e] border border-white/10 text-[#f5f5f5] text-sm font-mono focus:outline-none focus:border-[#3b82f6] mb-3" />
      <div className="flex gap-2">
        <Button onClick={() => onSave(prompt.key, val)} className="bg-[#3b82f6] text-white text-xs">Sauvegarder</Button>
        <Button onClick={() => { onReset(prompt.key); setVal(prompt.value); }} className="bg-[#1a1a2e] text-[#71717a] hover:text-[#a1a1aa] text-xs">Reset</Button>
      </div>
    </div>
  );
};

export default AdminBlog;
